import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { PlusOutline, Trash2Outline } from '@lyearn/icons';
import { AddBlock } from '@lyearn/molecules';
import { Button, IconButton, Input, Stack, Typography } from '@lyearn/ui';

export type ThreeFieldDynamicTextListItem = {
  title: string;
  description: string;
  kpis: string[];
};

export const ThreeFieldDelimiter = {
  TITLE_DESC: '@@',
  DESC_KPIS: '##',
  KPI: '%%',
};

const TwoFieldDynamicTextList = ({
  value,
  onChange,
  header,
}: {
  value: ThreeFieldDynamicTextListItem[];
  onChange: (value: ThreeFieldDynamicTextListItem[]) => void;
  header: {
    title: string;
    description: string;
    placeholder?: string;
  };
}) => {
  const { t } = useTranslation('Task');
  const onChangeText = useCallback(
    (index: number, field: keyof Omit<ThreeFieldDynamicTextListItem, 'kpis'>, newValue: string) => {
      const newValues = [...value];
      newValues[index] = {
        ...newValues[index],
        [field]: newValue || `${header?.title} ${index + 1}`, // Default to numbered title if empty
      };
      onChange(newValues);
    },
    [onChange, value, header?.title],
  );

  const onChangeKPI = useCallback(
    (itemIndex: number, kpiIndex: number, newValue: string) => {
      const newValues = [...value];
      newValues[itemIndex] = {
        ...newValues[itemIndex],
        kpis: newValues[itemIndex].kpis.map((kpi, idx) => (idx === kpiIndex ? newValue : kpi)),
      };
      onChange(newValues);
    },
    [onChange, value],
  );

  const onDeleteItem = useCallback(
    (index: number) => {
      const newValues = [...value];
      newValues.splice(index, 1);
      // Update titles for remaining items to maintain sequence
      // newValues.forEach((item, idx) => {
      //   if (!item.title || item.title.startsWith(header?.title)) {
      //     item.title = `${header?.title} ${idx + 1}`;
      //   }
      // });
      onChange(newValues);
    },
    [onChange, value, header?.title],
  );

  const onDeleteKPI = useCallback(
    (itemIndex: number, kpiIndex: number) => {
      const newValues = [...value];
      newValues[itemIndex] = {
        ...newValues[itemIndex],
        kpis: newValues[itemIndex].kpis.filter((_, idx) => idx !== kpiIndex),
      };
      onChange(newValues);
    },
    [onChange, value],
  );

  const addKPI = useCallback(
    (itemIndex: number) => {
      const newValues = [...value];
      newValues[itemIndex] = {
        ...newValues[itemIndex],
        kpis: [...newValues[itemIndex].kpis, ''],
      };
      onChange(newValues);
    },
    [onChange, value],
  );

  const renderKPIs = (itemIndex: number, kpis: string[]) => {
    return (
      <Stack direction="col" gapY={10}>
        {kpis.map((kpi, kpiIndex) => (
          <Input
            key={kpiIndex}
            fullWidth
            multiline
            className="bg-bg-secondary"
            endAdornment={
              kpis.length === 1 ? null : (
                <IconButton>
                  <Trash2Outline
                    fillColorToken="icon-placeholder"
                    height={20}
                    width={20}
                    onClick={() => onDeleteKPI(itemIndex, kpiIndex)}
                  />
                </IconButton>
              )
            }
            placeholder={header?.placeholder || `Add ${header?.title}`}
            size="large"
            value={kpi}
            onBlur={() => onChange(value)}
            onChange={(e) => onChangeKPI(itemIndex, kpiIndex, e.target.value)}
          />
        ))}
        <AddBlock
          className="h-[44px]"
          title={header?.placeholder || `Add ${header?.title}`}
          onClick={() => addKPI(itemIndex)}
        />
      </Stack>
    );
  };

  const renderItem = (index: number, item: ThreeFieldDynamicTextListItem) => {
    return (
      <Stack className="flex-1">
        <Stack className="w-full" direction="col" gapY={16}>
          <Stack className="w-full" direction="col" gapY={8}>
            {value?.length > 1 && (
              <Stack alignItems="center" className="w-full" direction="row" justifyContent="end">
                <IconButton>
                  <Trash2Outline
                    fillColorToken="icon-placeholder"
                    height={20}
                    width={20}
                    onClick={() => onDeleteItem(index)}
                  />
                </IconButton>
              </Stack>
            )}
            <Input
              fullWidth
              multiline
              className="bg-bg-secondary"
              placeholder="Add Title"
              size="large"
              value={item.title || `${header?.title} ${index + 1}`}
              onBlur={() => onChange(value)}
              onChange={(e) => onChangeText(index, 'title', e.target.value)}
            />
          </Stack>

          <Stack className="w-full" direction="col" gapY={8}>
            <Input
              fullWidth
              multiline
              className="bg-bg-secondary"
              placeholder={t('Task:AddDescription')}
              size="large"
              value={item.description}
              onBlur={() => onChange(value)}
              onChange={(e) => onChangeText(index, 'description', e.target.value)}
            />
          </Stack>

          <Stack className="w-full pt-16" direction="col" gapY={8}>
            {header?.placeholder && (
              <Typography className="text-text-secondary ms-8" variant="heading-xs">
                {header.placeholder}
              </Typography>
            )}
            {renderKPIs(index, item.kpis)}
          </Stack>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack direction="col" gapY={16}>
      <Stack className="pl-8" direction="col" gapY={8}>
        <Typography variant="heading-s">{header?.title}</Typography>
        <Typography className="text-text-secondary" variant="body-short-01">
          {header?.description}
        </Typography>
      </Stack>
      <Stack direction="col" gapY={10}>
        {value?.map((item, index) => (
          <Stack
            key={index}
            className="items-start"
            direction="row"
            gapX={8}
            style={{ marginBottom: index !== value.length - 1 ? 32 : 0 }}>
            {renderItem(index, item)}
          </Stack>
        ))}
        <Stack>
          <Button
            className="h-[44px] mt-24 max-w-[300px]"
            startIcon={<PlusOutline />}
            variant="outlined"
            onClick={() => {
              const newIndex = value.length;
              onChange([
                ...value,
                {
                  title: `${header?.title} ${newIndex + 1}`,
                  description: '',
                  kpis: [''],
                },
              ]);
            }}>
            {`Add ${header?.title}`}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default TwoFieldDynamicTextList;
